import { Font } from '@react-pdf/renderer';
import {
  chunkLongToken,
  germanHyphenateWord,
  germanSafeWrapCallback,
} from './germanHyphenation';

/**
 * react-pdf hyphenation is global. Template modules import order can override each other
 * (e.g. SQUAD's long-word splitter overwrites SHV's no-op). Re-assert per document render.
 */

/** Disable automatic hyphenation — avoids "ausgetauscht-" / orphan "." on German copy. */
export function registerReactPdfNoHyphenation(): void {
  Font.registerHyphenationCallback((word) => [word]);
}

/**
 * German TeX/Typst hyphenation for Monatskommentar Text nodes.
 * Empty parts must never be returned — react-pdf treats them as stretchable glue (visible spaces).
 */
export const germanHyphenationCallback = (word: string | null): string[] => {
  const parts = germanHyphenateWord(word).filter((p) => p !== '');
  if (!parts.length) return word == null ? [] : [word];
  return parts;
};

/** Register German hyphenation globally (SHV/PVM Monatskommentar-heavy layouts). */
export function registerReactPdfGermanHyphenation(): void {
  Font.registerHyphenationCallback(germanHyphenationCallback);
}

/**
 * Allow wrapping very long tokens (URLs, compounds) without inserting a visible hyphen,
 * and never detach trailing punctuation onto the next line.
 *
 * @deprecated Breaks inside the word stem ("Mindestkapita-lbeteiligungsquote").
 * Use `registerReactPdfGermanSafeWrap` for German body copy.
 */
export function registerReactPdfSafeLongWordWrap(maxChunk = 14): void {
  Font.registerHyphenationCallback((word) => chunkLongToken(word, maxChunk));
}

/**
 * German hyphenation with a fallback chunker for tokens that have no break points.
 *
 * Plain chunking (`registerReactPdfSafeLongWordWrap`) cuts after a fixed number of
 * characters and react-pdf paints a hyphen at the break — which yields
 * "Mindestkapita-lbeteiligungsquote" or "Computacente-r". So try the German Liang
 * patterns first; only when they find no break point (URL, ISIN, number) does the
 * chunker step in, so long tokens still cannot overflow.
 */
export function registerReactPdfGermanSafeWrap(maxChunk = 14): void {
  Font.registerHyphenationCallback((word) => germanSafeWrapCallback(word, maxChunk));
}

export { germanSafeWrapCallback };
